import { queryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { ResultOf } from '#features/api/utils/graphql';
import { isDefined } from '#utils/isDefined';

const getGamesDoc = graphql(`
  query GetAllGames {
    game {
      games {
        __typename
        ... on Game {
          id
          logo
          name
          mobileImage
          desktopImage
          icon
          slug
          platformsIds
          iconColor
          socialPreviewLeft
          socialPreviewRight
          session
          providers {
            id
            name
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export type Game = ResultOf<typeof getGamesDoc>['game']['games'][number];

export const getGames = () =>
  gqlFetcher(getGamesDoc).then((data) =>
    data?.game?.games.filter((game) => isDefined(game) && game.__typename === 'Game')
  );

export const getGamesQueryOptions = () =>
  queryOptions({
    queryKey: ['games'],
    queryFn: () => getGames(),
    staleTime: 1000 * 60 * 5,
  });

const getGameSettingsDoc = graphql(`
  query GameSettings($id: UUID!) {
    game {
      byId(id: $id) {
        __typename
        ... on Game {
          settings {
            key
            multiple
            title
            viewType
            inputType
            filterShown
            maxSelect
            minSelect
            settingId
            helperText
            pages
            previews
            socials

            values {
              id
              code
              badgeImage
              decorImage
              tagImage
              name
            }
          }
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const getGameSettings = (input: { id: string }) =>
  gqlFetcher(getGameSettingsDoc, input).then((data) =>
    data?.game?.byId?.__typename === 'Game' ? data.game.byId?.settings : null
  );

export const getGameSettingsQueryOptions = (input: { id: string }) =>
  queryOptions({
    queryKey: ['gameSettings', input.id],
    queryFn: () => getGameSettings(input),
  });

const getPlatformsDoc = graphql(`
  query GetAllPlatforms {
    game {
      allPlatforms {
        ... on PlatformResult {
          id
          name
        }
      }
    }
  }
`);

export const getPlatforms = () =>
  gqlFetcher(getPlatformsDoc).then((data) => data?.game?.allPlatforms?.filter(isDefined));

export const getPlatformsQueryOptions = () =>
  queryOptions({
    queryKey: ['platforms'],
    queryFn: () => getPlatforms(),
    staleTime: 1000 * 60 * 20,
  });
